import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useBoardStore } from '../store/boardStore';
import { useAuthStore } from '../store/authStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { useOrganizationStore } from '../store/organizationStore';
import { useToast } from '../contexts/ToastContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { usePageTitle } from '../hooks/usePageTitle';
import { Team, BoardListItem } from '../types';
import Select from '../components/Select';
import { trackClick } from '../utils/analytics';
import { getAiInsights, AiInsights } from '../utils/api';

type Board = BoardListItem;

interface CreateBoardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { title: string; description: string; template: string; team?: string }) => void;
}

// Template definitions for the selector
const BOARD_TEMPLATES = [
  { id: 'basic_retro', name: 'Basic Retrospective', description: 'Classic retrospective: Went Well, To Improve, Action Items.', icon: '🔄', columns: [{ title: 'Went Well', color: '#10B981' }, { title: 'To Improve', color: '#EF4444' }, { title: 'Action Items', color: '#3B82F6' }] },
  { id: 'start_stop_continue', name: 'Start, Stop, Continue', description: 'What to start, stop, and continue doing.', icon: '🚦', columns: [{ title: 'Start', color: '#10B981' }, { title: 'Stop', color: '#EF4444' }, { title: 'Continue', color: '#F59E0B' }] },
  { id: 'plus_delta', name: 'Plus / Delta', description: 'What worked well and what to change.', icon: '➕', columns: [{ title: 'Plus', color: '#10B981' }, { title: 'Delta', color: '#3B82F6' }] },
  { id: 'mad_sad_glad', name: 'Mad, Sad, Glad', description: 'Express emotions about the sprint.', icon: '😊', columns: [{ title: 'Mad', color: '#EF4444' }, { title: 'Sad', color: '#6B7280' }, { title: 'Glad', color: '#10B981' }] },
  { id: 'four_ls', name: '4Ls', description: 'Liked, Learned, Lacked, Longed For.', icon: '📚', columns: [{ title: 'Liked', color: '#10B981' }, { title: 'Learned', color: '#3B82F6' }, { title: 'Lacked', color: '#EF4444' }, { title: 'Longed For', color: '#8B5CF6' }] },
  { id: 'sailboat', name: 'Sailboat', description: 'Retrospective using Wind, Anchor, Rocks, and Island metaphor.', icon: '⛵', columns: [{ title: 'Wind', color: '#06B6D4' }, { title: 'Anchor', color: '#6B7280' }, { title: 'Rocks', color: '#EF4444' }, { title: 'Island', color: '#10B981' }] },
  { id: 'starfish', name: 'Starfish', description: 'Keep, Start, More Of, Less Of, Stop.', icon: '⭐', columns: [{ title: 'Keep', color: '#10B981' }, { title: 'Start', color: '#06B6D4' }, { title: 'More Of', color: '#3B82F6' }, { title: 'Less Of', color: '#F59E0B' }, { title: 'Stop', color: '#EF4444' }] },
  { id: 'good_bad_start_stop', name: 'Good / Bad / Start / Stop', description: 'Classic four-panel retrospective.', icon: '📋', columns: [{ title: 'Good', color: '#10B981' }, { title: 'Bad', color: '#EF4444' }, { title: 'Start', color: '#3B82F6' }, { title: 'Stop', color: '#F59E0B' }] },
  { id: 'the_good_the_bad_ugly', name: 'The Good, The Bad, The Ugly', description: 'Western-themed three-column retro.', icon: '🤠', columns: [{ title: 'The Good', color: '#10B981' }, { title: 'The Bad', color: '#EF4444' }, { title: 'The Ugly', color: '#6B7280' }] },
  { id: 'keep_problems_try', name: 'Keep / Problems / Try', description: 'Keep what works, solve problems, experiment.', icon: '🔧', columns: [{ title: 'Keep', color: '#10B981' }, { title: 'Problems', color: '#EF4444' }, { title: 'Try', color: '#3B82F6' }] },
  { id: 'hopes_and_concerns', name: 'Hopes & Concerns', description: 'What you hope for and worry about.', icon: '🌤️', columns: [{ title: 'Hopes', color: '#10B981' }, { title: 'Concerns', color: '#EF4444' }] },
  { id: 'plus_minus_interesting', name: 'Plus / Minus / Interesting', description: 'PMI — plus, minus, and interesting points.', icon: '🔍', columns: [{ title: 'Plus', color: '#10B981' }, { title: 'Minus', color: '#EF4444' }, { title: 'Interesting', color: '#F59E0B' }] },
  { id: 'i_like_i_wish', name: 'I Like / I Wish', description: 'Appreciation and improvement suggestions.', icon: '💬', columns: [{ title: 'I Like', color: '#10B981' }, { title: 'I Wish', color: '#3B82F6' }] },
  { id: 'i_like_i_wish_what_if', name: 'I Like / I Wish / What If', description: 'Appreciation, wishes, and creative ideas.', icon: '💡', columns: [{ title: 'I Like', color: '#10B981' }, { title: 'I Wish', color: '#3B82F6' }, { title: 'What If', color: '#8B5CF6' }] },
  { id: 'wwwh', name: 'WWWH — 4Ws', description: 'Went Well, Weird, Worrying, We Want.', icon: '❓', columns: [{ title: 'Went Well', color: '#10B981' }, { title: 'Was Weird', color: '#F59E0B' }, { title: 'Was Worrying', color: '#EF4444' }, { title: 'We Want', color: '#3B82F6' }] },
  { id: 'daki', name: 'DAKI', description: 'Drop, Add, Keep, Improve.', icon: '🎯', columns: [{ title: 'Drop', color: '#EF4444' }, { title: 'Add', color: '#10B981' }, { title: 'Keep', color: '#06B6D4' }, { title: 'Improve', color: '#F59E0B' }] },
  { id: 'kalm', name: 'KALM', description: 'Keep, Add, Less, More.', icon: '🧘', columns: [{ title: 'Keep', color: '#10B981' }, { title: 'Add', color: '#3B82F6' }, { title: 'Less', color: '#EF4444' }, { title: 'More', color: '#06B6D4' }] },
  { id: 'hot_air_balloon', name: 'Hot Air Balloon', description: 'Propellers, Sandbags, Sun, Storm metaforuyla.', icon: '🎈', columns: [{ title: 'Propellers', color: '#10B981' }, { title: 'Sandbags', color: '#EF4444' }, { title: 'Sun', color: '#F59E0B' }, { title: 'Storm', color: '#6B7280' }] },
  { id: 'speed_car', name: 'Speed Car', description: 'Engine ve Parachute metaforuyla.', icon: '🏎️', columns: [{ title: 'Engine', color: '#10B981' }, { title: 'Parachute', color: '#EF4444' }] },
  { id: 'rose_thorn_bud', name: 'Rose, Thorn, Bud', description: 'Highlights, Challenges, Opportunities.', icon: '🌹', columns: [{ title: 'Rose', color: '#EC4899' }, { title: 'Thorn', color: '#EF4444' }, { title: 'Bud', color: '#10B981' }] },
  { id: 'mountain_climber', name: 'Mountain Climber', description: 'Climbing, Valley, Peak Ahead.', icon: '⛰️', columns: [{ title: 'Climbing', color: '#10B981' }, { title: 'Valley', color: '#EF4444' }, { title: 'Peak Ahead', color: '#8B5CF6' }] },
  { id: 'lean_coffee', name: 'Lean Coffee', description: 'To Discuss, Discussing, Discussed.', icon: '☕', columns: [{ title: 'To Discuss', color: '#3B82F6' }, { title: 'Discussing', color: '#F59E0B' }, { title: 'Discussed', color: '#10B981' }] },
  { id: 'sprint_planning', name: 'Sprint Planning', description: 'Goals, Stories, Dependencies, Risks.', icon: '🗓️', columns: [{ title: 'Goals', color: '#3B82F6' }, { title: 'User Stories', color: '#10B981' }, { title: 'Dependencies', color: '#F59E0B' }, { title: 'Risks', color: '#EF4444' }] },
  { id: 'simple_kanban', name: 'Simple Kanban', description: 'To Do, In Progress, Done.', icon: '📌', columns: [{ title: 'To Do', color: '#6B7280' }, { title: 'In Progress', color: '#F59E0B' }, { title: 'Done', color: '#10B981' }] },
  { id: 'swot', name: 'SWOT Analysis', description: 'Strengths, Weaknesses, Opportunities, Threats.', icon: '📊', columns: [{ title: 'Strengths', color: '#10B981' }, { title: 'Weaknesses', color: '#EF4444' }, { title: 'Opportunities', color: '#3B82F6' }, { title: 'Threats', color: '#F59E0B' }] },
  { id: 'okrs', name: 'OKRs', description: 'Objectives, Key Results, Progress, Blockers.', icon: '🎯', columns: [{ title: 'Objectives', color: '#3B82F6' }, { title: 'Key Results', color: '#10B981' }, { title: 'Progress', color: '#F59E0B' }, { title: 'Blockers', color: '#EF4444' }] },
  { id: 'moscow', name: 'MoSCoW', description: 'Must Have, Should Have, Could Have, Won\'t Have.', icon: '🏛️', columns: [{ title: 'Must Have', color: '#EF4444' }, { title: 'Should Have', color: '#F97316' }, { title: 'Could Have', color: '#F59E0B' }, { title: "Won't Have", color: '#6B7280' }] },
  { id: 'eisenhower_box', name: 'Eisenhower Box', description: 'Urgent/Important matrix.', icon: '⬜', columns: [{ title: 'Urgent & Important', color: '#EF4444' }, { title: 'Not Urgent & Important', color: '#10B981' }, { title: 'Urgent & Not Important', color: '#F59E0B' }, { title: 'Not Urgent & Not Important', color: '#6B7280' }] },
  { id: 'five_whys', name: '5 Whys', description: 'Root cause analysis through repetitive questioning.', icon: '🤔', columns: [{ title: 'Problem', color: '#EF4444' }, { title: 'Why 1', color: '#F59E0B' }, { title: 'Why 2', color: '#F97316' }, { title: 'Why 3', color: '#EF4444' }, { title: 'Why 4', color: '#DC2626' }, { title: 'Root Cause', color: '#3B82F6' }] },
  { id: 'incident_post_mortem', name: 'Incident Post-Mortem', description: 'What Happened, Timeline, Root Cause, Actions, Prevention.', icon: '🔥', columns: [{ title: 'What Happened', color: '#EF4444' }, { title: 'Timeline', color: '#6B7280' }, { title: 'Root Cause', color: '#DC2626' }, { title: 'Actions', color: '#3B82F6' }, { title: 'Prevention', color: '#10B981' }] },
  { id: 'fibonacci_estimation', name: 'Fibonacci Estimation', description: 'Story point estimation with the Fibonacci sequence.', icon: '🔢', columns: [{ title: '1', color: '#10B981' }, { title: '2', color: '#06B6D4' }, { title: '3', color: '#3B82F6' }, { title: '5', color: '#8B5CF6' }, { title: '8', color: '#F59E0B' }, { title: '13', color: '#F97316' }, { title: '21', color: '#EF4444' }, { title: '?', color: '#6B7280' }] },
  { id: 'tshirt_estimation', name: 'T-Shirt Estimation', description: 'XS, S, M, L, XL sizing.', icon: '👕', columns: [{ title: 'XS', color: '#10B981' }, { title: 'S', color: '#06B6D4' }, { title: 'M', color: '#3B82F6' }, { title: 'L', color: '#F59E0B' }, { title: 'XL', color: '#EF4444' }] },
  { id: 'kudos_tree', name: 'Kudos Tree', description: 'Kudos, Shoutouts, Thank You.', icon: '🌳', columns: [{ title: 'Kudos', color: '#10B981' }, { title: 'Shoutouts', color: '#F59E0B' }, { title: 'Thank You', color: '#EC4899' }] },
  { id: 'weather_check_in', name: 'Weather Check-In', description: 'How the team feels using a weather metaphor.', icon: '⛅', columns: [{ title: 'Sunny', color: '#F59E0B' }, { title: 'Cloudy', color: '#6B7280' }, { title: 'Rainy', color: '#3B82F6' }, { title: 'Stormy', color: '#EF4444' }] },
  { id: 'roman_retro', name: 'Roman Retro', description: 'A Roman-themed retro guiding your team through history.', icon: '🏛️', columns: [{ title: 'Roman Identity', color: '#8B5CF6' }, { title: 'The Story', color: '#6B7280' }, { title: 'Senate Debate', color: '#EF4444' }, { title: 'Victories', color: '#F59E0B' }] },
  { id: 'custom', name: 'Custom Board', description: 'Start with empty columns and customize.', icon: '✨', columns: [{ title: 'Column 1', color: '#3B82F6' }, { title: 'Column 2', color: '#10B981' }, { title: 'Column 3', color: '#F59E0B' }] },
];

const TEMPLATE_BADGE: Record<string, { label: string; color: string }> = {
  start_stop_continue: { label: '🚦 Start/Stop/Continue', color: '#f97316' },
  mad_sad_glad: { label: '😊 Mad/Sad/Glad', color: '#8b5cf6' },
  four_ls: { label: '📚 4Ls', color: '#3b82f6' },
  basic_retro: { label: '🔄 Basic Retro', color: '#f59e0b' },
  sailboat: { label: '⛵ Sailboat', color: '#06b6d4' },
  starfish: { label: '⭐ Starfish', color: '#ef4444' },
};

const timeAgo = (dateString: string): string => {
  const diff = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

const CreateBoardModal: React.FC<CreateBoardModalProps> = ({ isOpen, onClose, onSubmit }) => {
  useOrganizationStore();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    template: 'start_stop_continue',
    team: '' as string | undefined
  });

  // Fetch teams when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchTeams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Auto-select team if only one team exists
  useEffect(() => {
    if (teams.length === 1 && !formData.team) {
      setFormData(prev => ({ ...prev, team: teams[0].id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams]);

  const fetchTeams = async () => {
    setLoadingTeams(true);
    try {
      const authState = useAuthStore.getState();
      const token = authState.tokens?.access;
      
      if (!token) {
        throw new Error('No access token available');
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${API_BASE_URL}/teams/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch teams');
      }

      const data = await response.json();
      const teamsList = data.results || data;
      setTeams(teamsList);
      
      // Auto-select first team if only one exists
      if (teamsList.length === 1) {
        setFormData(prev => ({ ...prev, team: teamsList[0].id }));
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoadingTeams(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) return;
    onSubmit({
      title: formData.title,
      description: formData.description,
      template: formData.template,
      team: formData.team
    });
    setFormData({ title: '', description: '', template: 'start_stop_continue', team: undefined });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-slate-900 mb-5">Create New Board</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-5">
            <label className="block text-sm font-medium text-slate-600 mb-2">
              Board Name
            </label>
            <input
              type="text"
              required
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g. Sprint 24 Retrospective"
            />
          </div>

          <div className="mb-5">
            <label className="block text-sm font-medium text-slate-600 mb-2">
              Description (Optional)
            </label>
            <textarea
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none"
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description..."
            />
          </div>

          {teams.length > 0 && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-slate-600 mb-2">
                Team {teams.length > 1 && <span className="text-red-500">*</span>}
              </label>
              <Select
                value={formData.team || (teams.length === 1 ? teams[0].id : '')}
                onChange={(v) => setFormData({ ...formData, team: v })}
                disabled={teams.length === 1}
                size="lg"
                placeholder={teams.length > 1 ? 'Select a team' : undefined}
                options={teams.map((team) => ({ value: team.id, label: team.name }))}
              />
              {teams.length === 1 && (
                <p className="mt-1 text-xs text-slate-500">Only one team available</p>
              )}
            </div>
          )}

          {teams.length === 0 && !loadingTeams && (
            <div className="mb-5 p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-800">
              No teams found. Please create a team first.
            </div>
          )}

          {loadingTeams && (
            <div className="mb-5 text-sm text-slate-500 text-center">
              Loading teams...
            </div>
          )}

          {/* Template Selection */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-slate-600 mb-3">
              Template <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2 max-h-[240px] overflow-y-auto pr-1">
              {BOARD_TEMPLATES.map((template) => (
                <div
                  key={template.id}
                  onClick={() => setFormData({ ...formData, template: template.id })}
                  className={`
                    relative p-3 rounded-xl cursor-pointer transition-all duration-200
                    ${formData.template === template.id
                      ? 'bg-orange-50 border-2 border-orange-500 shadow-md'
                      : 'bg-slate-50 border-2 border-slate-200 hover:border-orange-300 hover:shadow-sm'
                    }
                  `}
                >
                  {/* Selected indicator */}
                  {formData.template === template.id && (
                    <div className="absolute top-2 right-2 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  
                  {/* Template icon and name */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{template.icon}</span>
                    <span className="font-semibold text-sm text-slate-800">{template.name}</span>
                  </div>
                  
                  {/* Column preview */}
                  <div className="flex flex-wrap gap-1">
                    {template.columns.map((col, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 rounded text-[10px] font-medium text-white"
                        style={{ backgroundColor: col.color }}
                      >
                        {col.title}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={teams.length > 1 && !formData.team}
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface EditBoardModalProps {
  isOpen: boolean;
  board: Board | null;
  onClose: () => void;
  onSubmit: (boardId: string, data: { title: string; description: string }) => void;
}

const EditBoardModal: React.FC<EditBoardModalProps> = ({ isOpen, board, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: ''
  });

  useEffect(() => {
    if (board) {
      setFormData({
        title: board.title,
        description: board.description || ''
      });
    }
  }, [board]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !board) return;
    onSubmit(board.id, formData);
    onClose();
  };

  if (!isOpen || !board) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-xl">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Edit Board</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-5">
            <label className="block text-sm font-medium text-slate-600 mb-2">
              Board Name
            </label>
            <input
              type="text"
              required
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Board name"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-600 mb-2">
              Description (Optional)
            </label>
            <textarea
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description..."
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface DeleteBoardModalProps {
  isOpen: boolean;
  board: Board | null;
  onClose: () => void;
  onConfirm: (boardId: string) => void;
}

const DeleteBoardModal: React.FC<DeleteBoardModalProps> = ({ isOpen, board, onClose, onConfirm }) => {
  if (!isOpen || !board) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-xl">
        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </div>
        
        <h2 className="text-xl font-bold text-slate-900 text-center mb-2">Delete Board</h2>
        <p className="text-slate-500 text-center mb-6">
          Are you sure you want to delete "<span className="font-medium text-slate-700">{board.title}</span>"? This action cannot be undone.
        </p>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm(board.id);
              onClose();
            }}
            className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

interface BoardMenuProps {
  board: Board;
  onEdit: (board: Board) => void;
  onDelete: (board: Board) => void;
}

const BoardMenu: React.FC<BoardMenuProps> = ({ board, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-8 w-40 bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-10">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              trackClick('DashboardPage_board_menu_edit');
              onEdit(board);
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
          >
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              trackClick('DashboardPage_board_menu_delete');
              onDelete(board);
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, tokens } = useAuthStore();
  const { boards, loading, error: boardsError, fetchBoards, createBoard, updateBoard, deleteBoard } = useBoardStore();
  const { currentSubscription, fetchCurrentSubscription } = useSubscriptionStore();
  const { showError, showSuccess } = useToast();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingBoard, setEditingBoard] = useState<Board | null>(null);

  useEffect(() => {
    if ((location.state as { openCreateBoard?: boolean } | null)?.openCreateBoard) {
      setIsCreateModalOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);
  const [deletingBoard, setDeletingBoard] = useState<Board | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [teamFilter, setTeamFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'created' | 'name'>('recent');
  const [insights, setInsights] = useState<AiInsights | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);

  usePageTitle('Dashboard');

  useEffect(() => {
    // Wait for a real access token before fetching — firing this while auth is
    // still hydrating (e.g. right after a fast page refresh) fails immediately
    // and leaves boardStore.error set with an empty boards list, which renders
    // as an indistinguishable "no boards yet" empty state below.
    if (!tokens?.access) return;
    fetchBoards();
    fetchCurrentSubscription();
    getAiInsights(tokens.access).then(setInsights).catch(() => {});

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    fetch(`${API_BASE_URL}/teams/`, {
      headers: { 'Authorization': `Bearer ${tokens.access}` },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => setTeams(data.results || data))
      .catch(() => {});
  }, [fetchBoards, fetchCurrentSubscription, tokens?.access]);

  const handleCreateBoard = async (data: { title: string; description: string; template: string; team?: string }) => {
    try {
      const board = await createBoard({
        title: data.title,
        description: data.description,
        template: data.template,
        team: data.team
      });
      showSuccess('Board Created', 'Your board has been created successfully.');
      fetchCurrentSubscription();
      fetchBoards();
      if (board?.id) {
        navigate(`/app/board/${board.id}`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create board';
      showError('Create Board Failed', errorMessage);
      console.error('Error creating board:', error);
    }
  };

  const handleUpdateBoard = async (boardId: string, data: { title: string; description: string }) => {
    try {
      await updateBoard(boardId, data);
      await fetchBoards();
    } catch (error) {
      console.error('Error updating board:', error);
    }
  };

  const handleDeleteBoard = async (boardId: string) => {
    try {
      await deleteBoard(boardId);
      showSuccess('Board Deleted', 'Your board has been deleted successfully.');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete board';
      showError('Delete Board Failed', errorMessage);
      console.error('Error deleting board:', error);
    }
  };

  // Derived stats
  const activeBoards = boards.filter(b => b.status === 'active').length;
  const completedBoards = boards.filter(b => b.status === 'locked' || b.status === 'archived').length;
  const completionRate = boards.length > 0 ? Math.round((completedBoards / boards.length) * 100) : 0;
  const healthScore = insights?.health_score ?? null;
  const openIssues = insights?.unresolved_action_items.length ?? null;
  const boardsThisMonth = currentSubscription?.boards_created_this_month ?? boards.filter(b => {
    const d = new Date(b.created_at);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  const isQuotaLimited = currentSubscription?.plan?.boards_per_month !== null && currentSubscription?.plan?.boards_per_month !== undefined;
  const quotaUsed = currentSubscription?.boards_created_this_month || 0;
  const quotaTotal = currentSubscription?.plan?.boards_per_month || 1;
  const quotaHit = isQuotaLimited && quotaUsed >= quotaTotal;

  // Filtered + sorted boards
  const displayedBoards = boards
    .filter(b => {
      if (filter === 'active' && b.status !== 'active') return false;
      if (filter === 'completed' && b.status !== 'locked' && b.status !== 'archived') return false;
      if (teamFilter === 'none' && b.team) return false;
      if (teamFilter !== 'all' && teamFilter !== 'none' && b.team !== teamFilter) return false;
      if (search && !b.title.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.title.localeCompare(b.title);
      if (sortBy === 'created') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      return new Date(b.last_activity || b.created_at).getTime() - new Date(a.last_activity || a.created_at).getTime();
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">
              Welcome back, {user?.first_name || 'there'}
            </h1>
            <p className="text-slate-500 text-sm">
              {activeBoards > 0
                ? `${activeBoards} active board${activeBoards > 1 ? 's' : ''} · ${boardsThisMonth} created this month`
                : 'No active boards yet — create your first one'}
            </p>
          </div>
          <button
            onClick={() => {
              trackClick('DashboardPage_button_new_board');
              if (currentSubscription?.can_create_board && !currentSubscription.can_create_board.can_create) {
                showError('Board Limit Reached', currentSubscription.can_create_board.error_message || 'You have reached your monthly board limit.');
                return;
              }
              setIsCreateModalOpen(true);
            }}
            aria-disabled={currentSubscription?.can_create_board ? !currentSubscription.can_create_board.can_create : false}
            title={currentSubscription?.can_create_board && !currentSubscription.can_create_board.can_create ? currentSubscription.can_create_board.error_message || 'You have reached your monthly board limit.' : undefined}
            className={`inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-xl transition-colors ${currentSubscription?.can_create_board && !currentSubscription.can_create_board.can_create ? 'opacity-50' : ''}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Board
          </button>
        </div>

        {/* Stats — 4 cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {/* Total boards */}
          <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-orange-100 rounded-lg flex items-center justify-center shrink-0">
                <svg className="w-3.5 h-3.5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Boards</p>
            </div>
            <p className="text-3xl font-bold text-slate-900">{boards.length}</p>
            {isQuotaLimited && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-400">{quotaUsed}/{quotaTotal} this month</span>
                  {quotaHit && (
                    <Link to="/app/subscription" onClick={() => trackClick('DashboardPage_link_upgrade_plan')} className="text-xs font-semibold text-orange-500 hover:text-orange-600">
                      Upgrade →
                    </Link>
                  )}
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1">
                  <div
                    className={`h-1 rounded-full transition-all ${quotaHit ? 'bg-red-500' : 'bg-orange-400'}`}
                    style={{ width: `${Math.min((quotaUsed / quotaTotal) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Completion rate */}
          <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-orange-100 rounded-lg flex items-center justify-center shrink-0">
                <svg className="w-3.5 h-3.5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Completed</p>
            </div>
            <p className="text-3xl font-bold text-slate-900">{completionRate}%</p>
            <p className="text-xs text-slate-400 mt-1">{completedBoards} of {boards.length} boards</p>
          </div>

          {/* Health score */}
          <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-orange-100 rounded-lg flex items-center justify-center shrink-0">
                <svg className="w-3.5 h-3.5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <div className="flex items-center gap-1 relative group/health">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Health</p>
                <svg className="w-3.5 h-3.5 text-slate-300 group-hover/health:text-slate-400 cursor-default shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="absolute left-0 top-full mt-2 w-60 bg-slate-800 text-white text-xs rounded-xl p-3 shadow-xl z-20 invisible group-hover/health:visible opacity-0 group-hover/health:opacity-100 transition-opacity duration-150 pointer-events-none">
                  <p className="font-semibold mb-1.5 text-slate-100">How is this calculated?</p>
                  <p className="text-slate-300 mb-2">A 0–100 score based on your last 10 AI reports:</p>
                  <ul className="space-y-1 text-slate-300">
                    <li><span className="text-white font-medium">50 pts</span> — average sentiment (how positive feedback was across sessions)</li>
                    <li><span className="text-white font-medium">50 pts</span> — recurring issue penalty (fewer repeating problems = higher score)</li>
                  </ul>
                  <div className="flex items-center gap-3 mt-2.5 pt-2 border-t border-slate-700 text-slate-300">
                    <span><span className="text-emerald-400">●</span> 70–100</span>
                    <span><span className="text-amber-400">●</span> 40–69</span>
                    <span><span className="text-red-400">●</span> 0–39</span>
                  </div>
                </div>
              </div>
            </div>
            {healthScore !== null ? (
              <>
                <p className={`text-3xl font-bold ${healthScore >= 70 ? 'text-emerald-500' : healthScore >= 40 ? 'text-amber-500' : 'text-red-500'}`}>
                  {healthScore}
                </p>
                <p className="text-xs text-slate-400 mt-1">out of 100</p>
              </>
            ) : (
              <>
                <p className="text-3xl font-bold text-slate-300">—</p>
                <Link to="/app/insights" className="text-xs text-orange-500 hover:text-orange-600 mt-1 block">Run AI reports</Link>
              </>
            )}
          </div>

          {/* Open action items */}
          <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-orange-100 rounded-lg flex items-center justify-center shrink-0">
                <svg className="w-3.5 h-3.5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Open Issues</p>
            </div>
            {openIssues !== null ? (
              <>
                <p className={`text-3xl font-bold ${openIssues > 0 ? 'text-amber-500' : 'text-emerald-500'}`}>
                  {openIssues}
                </p>
                <p className="text-xs text-slate-400 mt-1">action item{openIssues !== 1 ? 's' : ''} pending</p>
              </>
            ) : (
              <>
                <p className="text-3xl font-bold text-slate-300">—</p>
                <p className="text-xs text-slate-400 mt-1">no AI data yet</p>
              </>
            )}
          </div>
        </div>

        {/* AI insights mini-banner */}
        {insights && insights.total_sessions > 0 && (
          <div className="mb-6 flex items-center justify-between gap-4 px-5 py-3.5 bg-orange-50 border border-orange-100 rounded-2xl">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-lg shrink-0">✨</span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">
                  {insights.recurring_issues.length > 0
                    ? `${insights.recurring_issues.length} recurring issue${insights.recurring_issues.length > 1 ? 's' : ''} detected across ${insights.total_sessions} sessions`
                    : `${insights.total_sessions} AI sessions analysed — no recurring issues detected`}
                </p>
                {insights.recurring_issues[0] && (
                  <p className="text-xs text-slate-500 truncate mt-0.5">"{insights.recurring_issues[0]}"</p>
                )}
              </div>
            </div>
            <Link
              to="/app/insights"
              onClick={() => trackClick('DashboardPage_banner_view_insights')}
              className="text-sm font-semibold text-orange-500 hover:text-orange-600 shrink-0"
            >
              View insights →
            </Link>
          </div>
        )}

        {/* Boards section */}
        <div>
          {/* Controls row */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
            <h2 className="text-lg font-semibold text-slate-900 flex-1">Your Boards</h2>

            {/* Search */}
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search boards..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent w-44 transition-all"
              />
            </div>

            {/* Team filter */}
            {teams.length > 1 && (
              <Select
                value={teamFilter}
                onChange={(v) => {
                  trackClick('DashboardPage_team_filter_select', { event_label: v });
                  setTeamFilter(v);
                }}
                size="md"
                className="w-36"
                options={[
                  { value: 'all', label: 'All teams' },
                  ...teams.map((team) => ({ value: team.id, label: team.name })),
                  { value: 'none', label: 'No team' },
                ]}
              />
            )}

            {/* Sort */}
            <Select
              value={sortBy}
              onChange={(v) => setSortBy(v as typeof sortBy)}
              size="md"
              className="w-40"
              options={[
                { value: 'recent', label: 'Recent activity' },
                { value: 'created', label: 'Date created' },
                { value: 'name', label: 'Name A → Z' },
              ]}
            />

            {/* Filter tabs */}
            <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
              {(['all', 'active', 'completed'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => {
                    trackClick('DashboardPage_filter_select', { event_label: f });
                    setFilter(f);
                  }}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    filter === f ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Empty state */}
          {displayedBoards.length === 0 ? (
            boardsError && boards.length === 0 && filter === 'all' && !search ? (
              <div className="text-center py-16 bg-red-50 border border-red-100 rounded-2xl">
                <div className="w-14 h-14 mx-auto mb-4 bg-white border border-red-100 rounded-xl flex items-center justify-center shadow-sm">
                  <svg className="w-7 h-7 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                  </svg>
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-1">Couldn't load your boards</h3>
                <p className="text-slate-400 text-sm mb-6">{boardsError}</p>
                <button
                  onClick={() => {
                    trackClick('DashboardPage_button_retry_boards');
                    fetchBoards();
                  }}
                  className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-xl transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : (
            <div className="text-center py-16 bg-slate-50 border border-slate-100 rounded-2xl">
              <div className="w-14 h-14 mx-auto mb-4 bg-white border border-slate-100 rounded-xl flex items-center justify-center shadow-sm">
                <svg className="w-7 h-7 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-1">
                {search ? `No boards matching "${search}"` : filter === 'all' ? 'No boards yet' : `No ${filter} boards`}
              </h3>
              <p className="text-slate-400 text-sm mb-6">
                {filter === 'all' && !search ? 'Create your first retrospective board to get started' : ''}
              </p>
              {filter === 'all' && !search && (
                <button
                  onClick={() => {
                    trackClick('DashboardPage_button_create_board_empty');
                    setIsCreateModalOpen(true);
                  }}
                  className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-xl transition-colors"
                >
                  Create Board
                </button>
              )}
            </div>
            )
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayedBoards.map((board) => {
                const tmpl = TEMPLATE_BADGE[board.template || ''];
                return (
                  <div
                    key={board.id}
                    className="group bg-white border border-slate-200 rounded-2xl hover:border-orange-300 hover:shadow-md transition-all relative"
                  >
                    {/* Status left border */}
                    <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl ${
                      board.status === 'active' ? 'bg-orange-400' :
                      board.status === 'locked' ? 'bg-emerald-400' : 'bg-slate-200'
                    }`} />

                    <div className="p-5 pl-6">
                      {/* Menu */}
                      <div className="absolute top-3 right-3">
                        <BoardMenu board={board} onEdit={setEditingBoard} onDelete={setDeletingBoard} />
                      </div>

                      <Link
                        to={`/app/board/${board.id}`}
                        onClick={() => trackClick('DashboardPage_board_card_open', { event_label: board.id })}
                        className="block"
                      >
                        {/* Template + team badges */}
                        {(tmpl || board.team_name) && (
                          <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                            {tmpl && (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium"
                                style={{ backgroundColor: tmpl.color + '18', color: tmpl.color }}
                              >
                                {tmpl.label}
                              </span>
                            )}
                            {board.team_name && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-500">
                                {board.team_name}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Title */}
                        <h3 className="font-semibold text-slate-900 group-hover:text-orange-500 transition-colors truncate pr-8 mb-1">
                          {board.title}
                        </h3>

                        {board.description && (
                          <p className="text-slate-400 text-sm line-clamp-1 mb-3">{board.description}</p>
                        )}

                        {/* Footer */}
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-50">
                          <div className="flex items-center gap-2">
                            {board.online_participants > 0 && (
                              <span className="flex items-center gap-1 text-xs font-medium text-emerald-600">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                                {board.online_participants} online
                              </span>
                            )}
                            <span className="text-xs text-slate-400">
                              {timeAgo(board.last_activity || board.created_at)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-slate-400">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {board.participants?.length || 0}
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <CreateBoardModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateBoard}
      />

      <EditBoardModal
        isOpen={!!editingBoard}
        board={editingBoard}
        onClose={() => setEditingBoard(null)}
        onSubmit={handleUpdateBoard}
      />

      <DeleteBoardModal
        isOpen={!!deletingBoard}
        board={deletingBoard}
        onClose={() => setDeletingBoard(null)}
        onConfirm={handleDeleteBoard}
      />
    </div>
  );
};

export default DashboardPage;
